import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import QRCodeStyling from 'qr-code-styling';
import tinycolor from 'tinycolor2';

import { CONTAINER_TYPE_QR_CODE_PREFIX } from './EditContainerInfo';
import { ContainerType } from '../ContainerComponents/ContainerPage';
import { getImageUrl } from '@/utils/imageUrl';
import SvgIcon from '@/components/Icon/SvgIcon';
import { getMissingFieldsForLabel } from './containerTypeLabelUtils';

type ContainerTypeLabelProps = {
  containerType: ContainerType;
  dateStamp: string;
  size: 'normal' | 'small' | 'avery5164';
  hasRfid?: boolean;
  scale?: number;
  onReady?: () => void;
};

export function ContainerTypeLabel({
  containerType,
  dateStamp,
  size = 'normal',
  hasRfid = false,
  scale = 3,
  onReady,
}: ContainerTypeLabelProps) {
  const {
    id,
    imageUrl,
    name,
    sizeHelper,
    packingUnit,
    packingInstructions,
    hexCode,
    sku,
  } = containerType;

  const [qrCodeURL, setQrCodeURL] = useState<string | null>(null);
  const [qrImageLoaded, setQrImageLoaded] = useState(false);
  const [typeImageLoaded, setTypeImageLoaded] = useState(false);
  const [fontScalingComplete, setFontScalingComplete] = useState(false);

  const bottomSectionTextColor = tinycolor
    .mostReadable(tinycolor(hexCode), ['white', '#1C1A20'])
    .toHex8String();
  const imageUrlToUse = getImageUrl(2048, imageUrl);

  const missingFields = useMemo(
    () => getMissingFieldsForLabel(containerType),
    [containerType],
  );

  useEffect(() => {
    if (missingFields.length > 0) {
      onReady?.();
    }
  }, [missingFields.length, onReady]);

  // Check if everything is ready and call onReady
  useEffect(() => {
    if (
      qrCodeURL &&
      qrImageLoaded &&
      typeImageLoaded &&
      fontScalingComplete &&
      onReady
    ) {
      onReady();
    }
  }, [qrCodeURL, qrImageLoaded, typeImageLoaded, fontScalingComplete, onReady]);

  useEffect(() => {
    const qr = new QRCodeStyling({
      data: `${CONTAINER_TYPE_QR_CODE_PREFIX}${id}`,
      width: 1024,
      height: 1024,
      dotsOptions: {
        color: '#000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
      },
      backgroundOptions: {
        color: '#FFF',
      },
      margin: 0,
      qrOptions: {
        errorCorrectionLevel: 'M',
      },
    });
    let url: string | null = null;
    qr.getRawData('png').then((blob) => {
      url = URL.createObjectURL(blob as Blob);
      setQrCodeURL(url);
    });

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [id]);

  /**
   * IRL labels have 2 sizes:
   *  - normal: 154mm x 102mm = 1,5098039216
   *  - small (for small boxes for cutlery etc.): 76mm x 52mm -> 152mm x 104mm = 1,4615384615
   *  - avery5164: Avery 5164 sticker sheet (US Letter, 2x3 grid), each label 4" x 3.333" = 101.6mm x 84.67mm
   * We keep their ratio, but scale them way bigger for the sake of export quality.
   */
  const sizeMap = {
    normal: { width: 154, height: 102 },
    small: { width: 152, height: 104 },
    avery5164: { width: 101.6, height: 84.67 },
  };
  const base = sizeMap[size];
  const containerSize = {
    width: base.width * scale,
    height: base.height * scale,
  };
  const bottomSectionHeight = containerSize.height * BOTTOM_SECTION_RATIO;
  const bottomSectionPadding = LABEL_PADDING;
  const sizeInfoRowHeight = SIZE_INFO_ROW_HEIGHT * scale;
  const titlePaddingRight = hasRfid ? 20 * scale : 0;
  const bottomSectionVerticalPadding = bottomSectionPadding * 2;
  const bottomTextGroupHeight = BOTTOM_TEXT_GROUP_HEIGHT * scale;
  const maxTitleWidth =
    containerSize.width - bottomSectionPadding * 2 - titlePaddingRight;
  const maxTitleHeight =
    bottomSectionHeight -
    bottomSectionVerticalPadding -
    bottomTextGroupHeight -
    sizeInfoRowHeight;

  const styles = getStyles(scale);

  if (missingFields.length > 0) {
    return (
      <div
        style={{
          ...styles.labelContainer,
          ...containerSize,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}
      >
        <span>Cannot generate label.</span>
        <span>Missing required fields: {missingFields.join(', ')}</span>
      </div>
    );
  }

  const nameToUse = name
    .replace(sizeHelper, '')
    .replace('()', '')
    .replace('[]', '')
    .replace('{}', '')
    .trim();

  return (
    <div
      style={{
        ...styles.labelContainer,
        ...containerSize,
      }}
    >
      <div style={styles.topSection}>
        <img
          src={qrCodeURL ?? 'not_found.png'}
          style={styles.qrCode}
          alt=""
          onLoad={() => setQrImageLoaded(true)}
          onError={() => setQrImageLoaded(true)}
        />
        <div style={styles.centeredImageContainer}>
          <img
            src={imageUrlToUse}
            style={styles.typeImage}
            alt=""
            onLoad={() => setTypeImageLoaded(true)}
            onError={() => setTypeImageLoaded(true)}
          />
        </div>
        <img style={styles.vytalLogo} src="/vytal_logo.png" alt="" />
      </div>
      <div
        style={{
          flex: 1,
          backgroundColor: `#${hexCode.replace('#', '')}`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            color: bottomSectionTextColor,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            padding: 12,
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div
              style={{
                flex: '0 1 auto',
                minHeight: 0,
                maxHeight: `calc(100% - ${sizeInfoRowHeight}px)`,
                overflow: 'hidden',
              }}
            >
              <FatTitle
                text={nameToUse}
                color={bottomSectionTextColor}
                maxWidth={maxTitleWidth}
                maxHeight={maxTitleHeight}
                onReady={() => setFontScalingComplete(true)}
              />
            </div>
            <SizeInfoRow
              dateStamp={dateStamp}
              rowHeight={sizeInfoRowHeight}
              sizeHelper={sizeHelper}
              dateSizeStyle={styles.dateSize}
              rowTextStyle={styles.rowText}
              sizeSizeStyle={styles.sizeSize}
            />
          </div>
          <div
            style={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'right',
              ...styles.bottomText,
            }}
          >
            <span>
              {packingInstructions ? `${packingInstructions} = ` : ''}{' '}
              {packingUnit} pcs.
            </span>
            <span>VyEES SKU: {sku}</span>
          </div>
          {hasRfid && <SvgIcon name="rfid-badge" style={styles.rfidBadge} />}
        </div>
      </div>
    </div>
  );
}

function SizeInfoRow({
  dateStamp,
  rowHeight,
  sizeHelper,
  dateSizeStyle,
  rowTextStyle,
  sizeSizeStyle,
}: {
  dateStamp: string;
  rowHeight: number;
  sizeHelper: string;
  dateSizeStyle: React.CSSProperties;
  rowTextStyle: React.CSSProperties;
  sizeSizeStyle: React.CSSProperties;
}) {
  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: rowHeight,
        ...rowTextStyle,
      }}
    >
      <span style={sizeSizeStyle}>{sizeHelper}</span>
      <span style={{ textAlign: 'right', ...dateSizeStyle }}>
        Generated on {dateStamp}
      </span>
    </div>
  );
}

function FatTitle({
  text,
  color,
  maxWidth,
  maxHeight,
  onReady,
}: {
  text: string;
  color: string;
  maxWidth: number;
  maxHeight: number;
  onReady?: () => void;
}) {
  const onReadyRef = useRef(onReady);
  const [metrics, setMetrics] = useState<FatTitleMetrics | null>(null);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useLayoutEffect(() => {
    let isCancelled = false;
    const titleText = text.toUpperCase();

    const measureTitle = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) return;

      context.font = `900 ${FAT_TITLE_MEASURE_FONT_SIZE}px PPFormulaCondensed`;
      const textMetrics = context.measureText(titleText);
      const left = textMetrics.actualBoundingBoxLeft || 0;
      const right = textMetrics.actualBoundingBoxRight || textMetrics.width;
      const ascent =
        textMetrics.actualBoundingBoxAscent || FAT_TITLE_MEASURE_FONT_SIZE;
      const descent = textMetrics.actualBoundingBoxDescent || 0;
      const letterSpacingWidth =
        Math.max(titleText.length - 1, 0) * FAT_TITLE_LETTER_SPACING;

      if (isCancelled) return;

      setMetrics({
        width: Math.max(left + right, textMetrics.width) + letterSpacingWidth,
        height: ascent + descent + FAT_TITLE_BOTTOM_PADDING,
        x: left,
        y: ascent,
      });
    };

    const loadFontAndMeasure = async () => {
      const font = `900 ${FAT_TITLE_MEASURE_FONT_SIZE}px PPFormulaCondensed`;

      await document.fonts?.load(font, titleText);
      await (document.fonts?.ready ?? Promise.resolve());

      if (!isCancelled) {
        measureTitle();
      }
    };

    void loadFontAndMeasure();

    return () => {
      isCancelled = true;
    };
  }, [text]);

  const scaleFactor = metrics
    ? Math.min(maxWidth / metrics.width, maxHeight / metrics.height)
    : 0;
  const renderedWidth = metrics ? metrics.width * scaleFactor : 0;
  const renderedHeight = metrics ? metrics.height * scaleFactor : 0;
  const isReady = renderedWidth > 0 && renderedHeight > 0;

  useEffect(() => {
    if (isReady) {
      onReadyRef.current?.();
    }
  }, [isReady, renderedHeight]);

  return (
    <div
      style={{
        width: '100%',
        height: isReady ? renderedHeight : 0,
        overflow: 'hidden',
      }}
    >
      {isReady && metrics && (
        <svg
          width={renderedWidth}
          height={renderedHeight}
          viewBox={`0 0 ${metrics.width} ${metrics.height}`}
          preserveAspectRatio="xMinYMin meet"
          style={{ display: 'block' }}
        >
          <text
            x={metrics.x}
            y={metrics.y}
            dominantBaseline="alphabetic"
            fill={color}
            style={stylesForFatTitleText}
          >
            {text.toUpperCase()}
          </text>
        </svg>
      )}
    </div>
  );
}

type FatTitleMetrics = {
  width: number;
  height: number;
  x: number;
  y: number;
};

const FAT_TITLE_MEASURE_FONT_SIZE = 100;
const FAT_TITLE_LETTER_SPACING = 3.2;
const FAT_TITLE_BOTTOM_PADDING = 1;
const LABEL_TEXT_FONT_FAMILY =
  "'Inter Variable', Inter, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif";
const TOP_SECTION_RATIO = 0.5387;
const BOTTOM_SECTION_RATIO = 1 - TOP_SECTION_RATIO;
const LABEL_PADDING = 12;
const SIZE_INFO_ROW_HEIGHT = 10;
const BOTTOM_TEXT_GROUP_HEIGHT = 9.6;

const stylesForFatTitleText: React.CSSProperties = {
  fontFamily: 'PPFormulaCondensed',
  fontWeight: 900,
  fontSize: FAT_TITLE_MEASURE_FONT_SIZE,
  letterSpacing: FAT_TITLE_LETTER_SPACING,
};

const getInterFontStyle = (): React.CSSProperties => ({
  fontFamily: LABEL_TEXT_FONT_FAMILY,
  fontStyle: 'normal',
  fontWeight: 400,
});

const getTextSizeStyle = (
  fontSize: number,
  lineHeight: number,
): React.CSSProperties => ({
  fontSize,
  lineHeight: `${lineHeight}px`,
});

const getStyles = (scale: number): { [key: string]: React.CSSProperties } => ({
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: 'white',
    overflow: 'hidden',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  topSection: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: `${TOP_SECTION_RATIO * 100}%`,
  },
  qrCode: {
    height: '100%',
    aspectRatio: 1 / 1,
    padding: '1%',
    imageRendering: 'pixelated',
  },
  centeredImageContainer: {
    display: 'flex',
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeImage: {
    height: '100%',
    aspectRatio: 1,
  },
  vytalLogo: {
    position: 'absolute',
    height: '10%',
    top: '7%',
    right: '2.5%',
  },
  rowText: getInterFontStyle(),
  sizeSize: getTextSizeStyle(7 * scale, 8 * scale),
  bottomText: {
    ...getInterFontStyle(),
    ...getTextSizeStyle(4.2 * scale, 4.8 * scale),
  },
  dateSize: getTextSizeStyle(2 * scale, 2.4 * scale),
  rfidBadge: {
    position: 'absolute',
    top: 18 * scale * -0.5,
    right: '2.9%',
    width: 18 * scale,
    height: 18 * scale,
  },
});
